import { useLayoutEffect, useRef, type FC, type ReactNode } from 'react';
import type { Attachment } from '@/app/lib/types/files';
import { StageOmnibox } from './StageOmnibox.tsx';
import { useIntl } from 'react-intl';
import { messages } from '@/app/lib/localization/messages.ts';
import type { ComposerItem } from './composer-draft.ts';

export interface StageDockProps {
  placeholder?: string;
  narration: string;
  narrationVariant: 'status' | 'caption';
  voiceActive?: boolean;
  activity: ReactNode;
  ready: boolean;
  voiceEnabled?: boolean;
  isUserRequestPending: boolean;
  onSend: (text: string, files?: Attachment[], selections?: ComposerItem[]) => void;
  onStopStreaming?: () => void;
  onStartVoice?: () => void;
  /** Full overlay height, including the safe-area inset, for end-of-page clearance. */
  onHeightChange: (height: number) => void;
}

/** Persistent controls and optional activity. Opening details never remounts the input or canvas. */
export const StageDock: FC<StageDockProps> = ({
  placeholder,
  narration,
  narrationVariant,
  voiceActive = false,
  activity,
  ready,
  voiceEnabled = true,
  isUserRequestPending,
  onSend,
  onStopStreaming,
  onStartVoice,
  onHeightChange,
}) => {
  const intl = useIntl();
  const dockRef = useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Switching voice mode replaces the measured edge node.
  useLayoutEffect(() => {
    const dock = dockRef.current;
    if (!dock) {
      return;
    }
    const edge = dock.querySelector<HTMLElement>('[data-dock-backdrop-edge]');
    const measure = () => {
      const bounds = dock.getBoundingClientRect();
      const top = edge ? Math.max(0, edge.getBoundingClientRect().top - bounds.top) : 0;
      dock.style.setProperty('--stage-dock-backdrop-top', `${top}px`);
      onHeightChange(bounds.height);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(dock, { box: 'border-box' });
    if (edge) {
      observer.observe(edge, { box: 'border-box' });
    }
    return () => observer.disconnect();
  }, [onHeightChange, voiceActive]);

  const activityContent = voiceActive ? null : activity;
  return (
    <div ref={dockRef} className="stage-dock pointer-events-none z-10 min-w-0 pb-dock-inset">
      <div aria-hidden="true" className="stage-dock-backdrop" />
      <div className="mx-auto flex max-w-container-form flex-col gap-2">
        {activityContent}
        <StageOmnibox
          placeholder={placeholder ?? intl.formatMessage(messages.askAnything)}
          pending={isUserRequestPending}
          ready={ready}
          voiceEnabled={voiceEnabled}
          voiceLine={voiceActive ? narration : null}
          voiceLineStatus={narrationVariant === 'status'}
          onSend={onSend}
          onStop={() => onStopStreaming?.()}
          onStartVoice={onStartVoice}
        />
      </div>
    </div>
  );
};
